import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Accessible slide-over panel — BUILD-PLAN §27 (mobile filter drawer),
 * §40 (accessible dialogs), §44.
 *
 * Built on the native <dialog> element rather than a div with role="dialog".
 * The browser then provides focus trapping, Escape-to-close, inertness of the
 * background and the top-layer stacking for free — all of which are
 * error-prone to reimplement and commonly get shipped broken.
 */

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Drawer({ open, onClose, title, children, footer }: DrawerProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      // showModal (not show) is what makes the background inert.
      dialog.showModal();
      // Prevent the page behind from scrolling under the panel.
      document.body.style.overflow = 'hidden';
    } else if (!open && dialog.open) {
      dialog.close();
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      aria-label={title}
      // The browser fires `cancel` for Escape; forwarding it keeps React
      // state in sync with the element's real open state.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      className="m-0 h-full max-h-full w-[min(22rem,90vw)] max-w-full bg-surface p-0 text-ink backdrop:bg-black/40 ml-auto"
    >
      <div className="flex h-full flex-col">
        <header className="flex items-center justify-between border-b border-line px-5 py-4">
          <h2 className="font-display text-lg">{title}</h2>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-11 w-11 items-center justify-center text-ink transition-colors hover:text-accent"
          >
            <X className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-6">{children}</div>

        {footer && (
          <footer className="border-t border-line px-5 py-4">{footer}</footer>
        )}
      </div>
    </dialog>
  );
}
